/**
 * Supabase realtime — every open tab sees teammates' edits live.
 *
 * Subscribe once on boot (gated by `_channel` so duplicate calls are a
 * no-op). Each row change patches the local state AND the sync baseline
 * so our own next push doesn't re-emit the value.
 *
 * Tables in the subscription:
 *   - calendar / notes / meetings — per-day per-person edits
 *   - closures                    — admin-flagged company closure days
 *   - profiles                    — sign-in events + admin-role changes
 *                                   (only visible to admins via RLS, so
 *                                   non-admin clients receive nothing here)
 *   - roster                      — directory adds / updates / mark-former
 */
import { sb } from "../supabase";
import { state } from "./store";
import {
  markCellSynced,
  markRosterSynced,
  readBaselineCell,
  readBaselineRoster,
  setApplyingRealtime,
} from "./storage";
import { restGet } from "../utils/withRetry";
import { toast } from "../utils/toast";
import type {
  CalendarMap,
  NoteMap,
  MeetingMap,
  ProfileRow,
  ProfilesByRoster,
  RosterEntry,
  StatusCode,
} from "../types";
import type { RealtimeChannel } from "@supabase/supabase-js";

let _channel: RealtimeChannel | null = null;

interface PgChange {
  eventType: "INSERT" | "UPDATE" | "DELETE";
  new: Record<string, unknown> | null;
  old: Record<string, unknown> | null;
}

function applyChange(table: "calendar" | "notes" | "meetings", payload: PgChange): void {
  const row = (payload.new ?? payload.old ?? {}) as Record<string, unknown>;
  const person = row.person as string | undefined;
  const day = row.day as string | undefined;
  if (!person || !day) return;

  // Compute the broadcast value. For deletes the broadcast effectively
  // says "no value"; we represent that as null so the equality checks
  // below treat absent-cells uniformly across delete and non-delete
  // events.
  const isDelete = payload.eventType === "DELETE";
  let value: string | null = null;
  if (!isDelete) {
    if (table === "calendar") value = String(row.status ?? "");
    else if (table === "notes") value = String(row.text ?? "");
    else value = String(row.description ?? ""); // meetings
  }
  const broadcastValue = isDelete ? null : value;

  // STALE-ECHO GUARD. If the user edited this cell twice in quick
  // succession (or just once during slow network), the WAL echo of the
  // FIRST push can arrive AFTER state has already advanced to the
  // user's newer value. The old code overwrote state with the echo
  // unconditionally, silently discarding the newer in-memory edit.
  //
  // Strategy: read BOTH the live state value and the baseline value
  // BEFORE we touch the baseline. Three cases:
  //   (a) Local already matches the broadcast → just update the
  //       baseline; no state mutation needed.
  //   (b) Local differs from the broadcast AND local also differs from
  //       the OLD baseline → there's a pending local edit newer than
  //       this echo. Preserve local state; the pending edit will be
  //       pushed on the next pushOnce. Baseline still advances to the
  //       broadcast value, which is what the server actually stored
  //       for this echo.
  //   (c) Local differs from the broadcast AND local matched the OLD
  //       baseline → no pending edit. The broadcast is a true external
  //       write from another teammate (or another tab); accept it.
  const s = state.value;
  const bag = (table === "calendar"
    ? s.calendar
    : table === "notes"
      ? s.notes
      : s.meetings) as CalendarMap | NoteMap | MeetingMap;
  const localValue = (bag[person]?.[day] ?? null) as string | null;
  const baselineValue = readBaselineCell(table, person, day);

  // Always update the baseline. The broadcast confirms what the server
  // stored at this point — the next computeDiffs() must reflect that.
  markCellSynced(table, person, day, broadcastValue);

  if (localValue === broadcastValue) {
    // (a) state is already up-to-date with the echo
    return;
  }
  if (localValue !== baselineValue) {
    // (b) pending local edit — keep it. saveStore is gated by the
    // realtime flag for the baseline update above, but since we never
    // mutated state, saveStore won't fire from this path at all.
    return;
  }

  // (c) genuine external write — apply it. The isApplyingRealtime flag
  // suppresses the localStorage thrash + pushOnce invocation that the
  // saveStore effect would otherwise produce.
  setApplyingRealtime(true);
  try {
    const personMap = { ...(bag[person] ?? {}) };
    if (isDelete) {
      delete personMap[day];
    } else if (table === "calendar") {
      (personMap as Record<string, StatusCode>)[day] = value as StatusCode;
    } else {
      (personMap as Record<string, string>)[day] = value ?? "";
    }
    const updated: Record<string, Record<string, unknown>> = { ...bag, [person]: personMap };
    if (Object.keys(personMap).length === 0) delete updated[person];
    state.value = { ...s, [table]: updated as never } as typeof s;
  } finally {
    setApplyingRealtime(false);
  }
}

function applyClosureChange(payload: PgChange): void {
  const row = (payload.new ?? payload.old ?? {}) as Record<string, unknown>;
  const day = row.day as string | undefined;
  if (!day) return;
  // Closures aren't in the diff baseline (only calendar/notes/meetings/
  // roster are), so there's no markXSynced call needed — but we still
  // gate saveStore via the realtime flag to avoid the 50ms-per-broadcast
  // localStorage write + a pointless pushOnce snapshot.
  setApplyingRealtime(true);
  try {
    const s = state.value;
    const closures = { ...s.closures };
    if (payload.eventType === "DELETE") {
      delete closures[day];
    } else {
      closures[day] = String(row.label ?? "");
    }
    state.value = { ...s, closures };
  } finally {
    setApplyingRealtime(false);
  }
}

/** Profiles — events arrive only for rows the current viewer can see
 *  via RLS (own row for users, all rows for admins). The Directory's
 *  Access column reads from `profilesByRoster`, so we keep that map
 *  in sync as new sign-ins happen and as admins promote / demote.
 *
 *  If the changed row is the SIGNED-IN USER'S OWN profile, also mirror
 *  the admin / super-admin flags into top-level state. Without that
 *  step, a freshly-promoted user kept seeing the regular-user UI
 *  (no Dashboard tab, no admin actions) until they manually refreshed
 *  — even though their pill in the Directory already showed "Admin"
 *  because profilesByRoster had updated correctly. That was the
 *  "I promoted Claudia but she doesn't see admin features" bug. */
function applyProfileChange(payload: PgChange): void {
  const row = (payload.new ?? payload.old ?? {}) as Record<string, unknown>;
  const rosterName = (payload.new?.roster_name ?? payload.old?.roster_name) as
    | string
    | null
    | undefined;
  if (!rosterName) return; // unlinked profile — nothing to render in Directory

  // Profiles aren't in the diff baseline either. Realtime flag gates
  // saveStore for the same reason as closures.
  setApplyingRealtime(true);
  let promotedToAdmin = false;
  let demotedToUser = false;
  try {
    const s = state.value;
    const profilesByRoster = { ...s.profilesByRoster };
    let isAdmin = s.isAdmin;
    let isSuperAdmin = s.isSuperAdmin;

    if (payload.eventType === "DELETE") {
      delete profilesByRoster[rosterName];
    } else {
      const p: ProfileRow = {
        id: String(row.id ?? ""),
        email: String(row.email ?? ""),
        roster_name: rosterName,
        is_admin: !!row.is_admin,
        is_super_admin: !!row.is_super_admin,
        password_set: !!row.password_set,
        // Equipment-email recipient flag (migration 23). Realtime sends
        // the full new row, but if the column is ever absent keep what we
        // already had rather than silently flipping the bell off.
        notify_equipment:
          row.notify_equipment === undefined
            ? s.profilesByRoster[rosterName]?.notify_equipment
            : !!row.notify_equipment,
      };
      profilesByRoster[rosterName] = p;
      // The mirror-into-top-level-state step: only for the user's own row.
      if (p.id && p.id === s.profileId) {
        if (s.isAdmin !== p.is_admin) {
          if (p.is_admin) promotedToAdmin = true;
          else demotedToUser = true;
          isAdmin = p.is_admin;
        }
        if (s.isSuperAdmin !== p.is_super_admin) {
          isSuperAdmin = p.is_super_admin;
        }
      }
    }
    state.value = { ...s, profilesByRoster, isAdmin, isSuperAdmin };
  } finally {
    setApplyingRealtime(false);
  }

  // Side effects after the state mutation has settled. Toast + hydrate
  // run outside the realtime flag so saveStore behaves normally for
  // anything they trigger.
  if (promotedToAdmin) {
    toast("You're now an admin — loading admin features…");
    void hydrateAdminProfiles();
  } else if (demotedToUser) {
    toast("Your admin access has been removed.");
  }
}

/** Re-fetch the full profilesByRoster cache from the DB and replace
 *  the in-memory copy.
 *
 *  Originally written as the post-promotion hydration step; now also
 *  used as a general staleness healer — called when the Directory
 *  tab opens, so any cache drift from missed realtime broadcasts /
 *  pre-fix optimistic UI lies is corrected without requiring users
 *  to refresh the page. Uses restGet, which bypasses the Supabase
 *  JS auth-token lock the same way our writes do.
 *
 *  Exported so views can trigger it. Silent on failure — if the fetch
 *  fails, the existing (possibly stale) cache stays in place rather
 *  than vanishing on the user. */
export async function refreshProfilesByRoster(): Promise<void> {
  try {
    // notify_equipment arrived with migration 23. Ask for it, but if the
    // column doesn't exist yet (client deployed ahead of the migration)
    // fall back to the original list so the Directory keeps working.
    const base = "select=id,email,roster_name,is_admin,is_super_admin,password_set";
    let rows: ProfileRow[];
    try {
      rows = await restGet<ProfileRow>("profiles", `${base},notify_equipment`);
    } catch {
      rows = await restGet<ProfileRow>("profiles", base);
    }
    const fresh: ProfilesByRoster = {};
    rows.forEach((p) => {
      if (p.roster_name) fresh[p.roster_name] = p;
    });
    // Replace wholesale: this IS the authoritative state, by design.
    // Anything missing from the fetch is missing from the DB and
    // shouldn't linger in the local cache.
    setApplyingRealtime(true);
    try {
      const s = state.value;
      state.value = { ...s, profilesByRoster: fresh };
    } finally {
      setApplyingRealtime(false);
    }
  } catch {
    // Silent — keep stale data rather than nuking it on a transient
    // network error.
  }
}

/** Background fetch of all profiles, fired when the current user is
 *  freshly promoted to admin. Delegates to refreshProfilesByRoster. */
async function hydrateAdminProfiles(): Promise<void> {
  await refreshProfilesByRoster();
}

/** Roster — same idea but for the roster map (unit, role, email,
 *  active flag, etc.). Keeps the Directory live as HR runs the
 *  SharePoint importer or admins edit a row.
 *
 *  Same baseline-sync pattern as applyChange: update the synced baseline
 *  FIRST, then state under the realtime flag, so saveStore doesn't fire
 *  a phantom upsert that would hit `roster_admin_write` RLS rejection
 *  for every non-admin tab. */
function applyRosterChange(payload: PgChange): void {
  const row = (payload.new ?? payload.old ?? {}) as Record<string, unknown>;
  const name = (payload.new?.name ?? payload.old?.name) as string | undefined;
  if (!name) return;

  const isDelete = payload.eventType === "DELETE";
  const entry: RosterEntry | null = isDelete
    ? null
    : {
        unit: String(row.unit ?? ""),
        role: String(row.role ?? ""),
        email: String(row.email ?? ""),
        active: row.active !== false,
        first_name: String(row.first_name ?? ""),
        last_name: String(row.last_name ?? ""),
        last_active_day: (row.last_active_day as string | null) ?? null,
      };

  // Same stale-echo guard as applyChange. Two admins editing the same
  // roster row in quick succession could otherwise lose the slower one's
  // edit when the faster one's WAL echo arrives. Only the mutable
  // fields (unit / role / email) feed the diff (see computeDiffs), so
  // those are what we compare.
  const s = state.value;
  const localEntry = s.roster[name] ?? null;
  const baselineEntry = readBaselineRoster(name);
  const localMatchesBroadcast = entriesMutableEqual(localEntry, entry);
  const localMatchesBaseline = entriesMutableEqual(localEntry, baselineEntry);

  markRosterSynced(name, entry);

  if (localMatchesBroadcast) return; // (a) already in sync
  if (!localMatchesBaseline) return; // (b) pending local edit, keep it

  // (c) genuine external write — apply it.
  setApplyingRealtime(true);
  try {
    const roster = { ...s.roster };
    if (isDelete) delete roster[name];
    else roster[name] = entry as RosterEntry;
    state.value = { ...s, roster };
  } finally {
    setApplyingRealtime(false);
  }
}

/** Compare two roster entries on the mutable fields (unit, role,
 *  email). The other fields (active, first_name, last_name,
 *  last_active_day) flow through the SharePoint importer's direct
 *  SQL — not the in-app diff — so they aren't part of "pending edit"
 *  detection. */
function entriesMutableEqual(a: RosterEntry | null, b: RosterEntry | null): boolean {
  if (a === b) return true;
  if (!a || !b) return false;
  return a.unit === b.unit && a.role === b.role && a.email === b.email;
}

export function subscribeRealtime(): void {
  if (_channel) return;
  _channel = sb
    .channel("presence-tracker")
    .on(
      "postgres_changes",
      { event: "*", schema: "public", table: "calendar" },
      (p) => applyChange("calendar", p as unknown as PgChange),
    )
    .on(
      "postgres_changes",
      { event: "*", schema: "public", table: "notes" },
      (p) => applyChange("notes", p as unknown as PgChange),
    )
    .on(
      "postgres_changes",
      { event: "*", schema: "public", table: "meetings" },
      (p) => applyChange("meetings", p as unknown as PgChange),
    )
    .on(
      "postgres_changes",
      { event: "*", schema: "public", table: "closures" },
      (p) => applyClosureChange(p as unknown as PgChange),
    )
    .on(
      "postgres_changes",
      { event: "*", schema: "public", table: "profiles" },
      (p) => applyProfileChange(p as unknown as PgChange),
    )
    .on(
      "postgres_changes",
      { event: "*", schema: "public", table: "roster" },
      (p) => applyRosterChange(p as unknown as PgChange),
    )
    .subscribe();
}

/** Tear down the subscription. Bounded by a 1-second timeout so a hung
 *  websocket can never freeze sign-out. The local channel reference is
 *  cleared synchronously up front; the await is best-effort cleanup
 *  (server-side leak is harmless — Supabase reaps idle channels). */
export async function unsubscribeRealtime(): Promise<void> {
  if (!_channel) return;
  const channel = _channel;
  _channel = null; // clear immediately — concurrent calls become no-ops
  try {
    await Promise.race([
      sb.removeChannel(channel),
      new Promise<void>((resolve) => setTimeout(resolve, 1000)),
    ]);
  } catch {
    /* ignore — we've already nulled _channel, server will reap eventually */
  }
}

/** True iff the realtime channel is currently connected. Used by the
 *  auth listener to decide whether boot has completed. */
export function isRealtimeConnected(): boolean {
  return _channel !== null;
}
